import config from "../../../../config";
import CodeBlock from "../../../../shared/CodeBlock/CodeBlock";

export default function ConsentRequirements({ stepNumber }) {
  const PDEX_URL = config.pdex_implementation_guide;

  const SampleRequest = {
    endpointName: "",
    request: `{
  "name": "Consent",
  "resource": {
    "resourceType": "Consent",
    "contained": [
      {
        "resourceType": "Patient",
        "id": "p2",
        "extension": [
          {
            "extension": [
              {
                "url": "ombCategory",
                "valueCoding": {
                  "system": "urn:oid:2.16.840.1.113883.6.238",
                  "code": "2028-9",
                  "display": "Asian"
                }
              },
              {
                "url": "text",
                "valueString": "Asian"
              }
            ],
            "url": "http://hl7.org/fhir/us/core/StructureDefinition/us-core-race"
          },
          {
            "extension": [
              {
                "url": "ombCategory",
                "valueCoding": {
                  "system": "urn:oid:2.16.840.1.113883.6.238",
                  "code": "2186-5",
                  "display": "Not Hispanic or Latino"
                }
              },
              {
                "url": "text",
                "valueString": "Not Hispanic or Latino"
              }
            ],
            "url": "http://hl7.org/fhir/us/core/StructureDefinition/us-core-ethnicity"
          },
          {
            "url": "http://hl7.org/fhir/us/core/StructureDefinition/us-core-birthsex",
            "valueCode": "M"
          }
        ],
        "identifier": [
          {
            "use": "usual",
            "type": {
              "coding": [
                {
                  "system": "http://terminology.hl7.org/CodeSystem/v2-0203",
                  "code": "MR",
                  "display": "Medical Record Number"
                }
              ],
              "text": "Medical Record Number"
            },
            "system": "http://hospital.smarthealthit.org",
            "value": "1032703"
          }
        ],
        "active": true,
        "name": [
          {
            "family": "Example",
            "given": [
              "Infant"
            ]
          }
        ],
        "telecom": [
          {
            "system": "phone",
            "value": "555-555-5555",
            "use": "home"
          }
        ],
        "gender": "male",
        "birthDate": "2020-06-02",
        "address": [
          {
            "line": [
              "49 Meadow St"
            ],
            "city": "Mounds",
            "state": "OK",
            "postalCode": "74047",
            "country": "US"
          }
        ]
      },
      {
        "resourceType": "Organization",
        "id": "p1",
        "identifier": [
          {
            "system": "http://hl7.org/fhir/sid/us-npi",
            "value": "1407071236"
          },
          {
            "system": "http://example.org/fhir/sid/us-tin",
            "value": "121111111"
          }
        ],
        "active": true,
        "type": [
          {
            "coding": [
              {
                "system": "http://terminology.hl7.org/CodeSystem/organization-type",
                "code": "prov",
                "display": "Healthcare Provider"
              }
            ]
          }
        ],
        "name": "Acme Clinic",
        "telecom": [
          {
            "system": "phone",
            "value": "(+1) 734-677-7777"
          },
          {
            "system": "email",
            "value": "customer-service@acme-clinic.org"
          }
        ],
        "address": [
          {
            "line": [
              "3300 Washtenaw Avenue, Suite 227"
            ],
            "city": "Amherst",
            "state": "MA",
            "postalCode": "01002",
            "country": "USA"
          }
        ]
      },
      {
        "resourceType": "DocumentReference",
        "id": "p1",
        "content": {
          "contentType": "text/html",
          "data": ""
        },
        "format": {
          "system": "urn:oid:1.3.6.1.4.1.19376.1.2.3",
          "code": "urn:hl7-org:sdwg:ccda-structuredBody:2.1",
          "display": "Documents following C-CDA constraints using a structured body."
        }
      }
    ]
  }
}`,
  };
  return (
    <div className="step-container">
      <div className="number-and-title">
        <h2 className="step-number">{stepNumber}</h2>
        <h2 className="step-title">Requirements for Consent</h2>
      </div>
      <ol className="step-content">
        <p>
          To access member data, it is vital to have a Consent FHIR resource,
          which should adhere to several prerequisites. We utilize the member
          match operation as described in the{" "}
          <a rel="noreferrer" href={`${PDEX_URL}`}>
            Pdex Implementation guide
          </a>{" "}
          to gain access to member claims as well as clinical data. The
          necessary requirements include:
        </p>
        <li>
          <p>Consent status</p>
        </li>
        <li>
          <p>Scope of consent - we anticipate it to be patient-privacy</p>
        </li>
        <li>
          <p>
            Patient reference - This must align with the patient information
            provided in the Patient resource during the member match operation
          </p>
        </li>
        <li>
          <p> Performer reference </p>
        </li>
        <li>
          <p>
            Source - The document reference provided should be a contained
            resource and cannot be a reference to the sender's FHIR server. The
            document's type is expected to be "patient-consent". The subject
            should be identical to the patient as stipulated in the
            consent/member match operation. The document's content cannot be
            null and must be inline data, not a binary reference to the source
            server.
          </p>
        </li>
        <li>
          <p>
            Policy - Possible values are "sensitive" or "normal". In case the
            policy is "sensitive", all member data including sensitive (for
            instance, SUD, Behavioral, HIV) will be shared. If the policy is
            "normal", the member's non-sensitive information will be shared.
          </p>
        </li>
        <li>
          <p>
            Provision - This field's value is expected to be either "deny" or
            "permit". If "deny" is received, we will reject the data request.
          </p>
        </li>
        <li>
          <p>
            Actor - We expect the data requesting organization to be the actor:
            recipient. The organization must be a contained resource and cannot
            be a reference to the sender's FHIR server. The request must include
            the organization's name, NPI, address, and type.
          </p>
        </li>
        <p>
          All the FHIR references to the source server must be resolved within
          the consent FHIR resource using the contained resource.
        </p>
        <p>
          A sample Consent resource is provided below for structural reference:
          <br />
          <CodeBlock
            title="Structural Reference"
            content={JSON.parse(SampleRequest.request)}
            endpointName={SampleRequest.endpointName}
            codeBlockType="jsonType"
          />
        </p>
      </ol>
    </div>
  );
}
